import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Sparkles, Brain, Orbit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BrandQuiz from './BrandQuiz';
import ParticleNetwork from './ParticleNetwork';
import AnimatedGrid from './AnimatedGrid';
import { useSounds } from './SoundEffects';

const Hero = () => {
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const { playSound } = useSounds();
  const navigate = useNavigate();

  // Allow external pages (like the 3D experience) to request the quiz be opened.
  // Supports both (a) live event dispatch for same-mount callers, and (b) a
  // sessionStorage flag for cross-route handoff where the listener may not
  // be registered in time.
  useEffect(() => {
    const handler = () => setQuizOpen(true);
    window.addEventListener('icd:open-quiz', handler);
    if (sessionStorage.getItem('icd_open_quiz') === '1') {
      sessionStorage.removeItem('icd_open_quiz');
      setQuizOpen(true);
    }
    return () => window.removeEventListener('icd:open-quiz', handler);
  }, []);

  const scrollToSection = (section) => {
    playSound('whoosh');
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleQuizComplete = (result) => {
    setQuizResult(result);
    // Store in sessionStorage to pass to contact form
    sessionStorage.setItem('quizResult', JSON.stringify(result));
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Layered Background Effects */}
      <div className="absolute inset-0 z-0">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-blue-950/20 to-slate-950"></div>
        
        {/* Animated grid and effects */}
        <AnimatedGrid />
        
        {/* Particle network */}
        <ParticleNetwork />
        
        {/* Hero image overlay */}
        <motion.div
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1771875797242-77f69f061132')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></motion.div>
        
        {/* Holographic overlay */}
        <div className="absolute inset-0 holographic opacity-20"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-8"
        >
          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight px-4"
          >
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-600 neon-text-large">
              Innovate. Design. Dominate.
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light px-4"
          >
            From visionary logos and immersive websites to complete marketing packages and custom merchandise —{' '}
            <span className="text-cyan-400 font-semibold">ICD cultivates brands</span> that don't just exist…{' '}
            <span className="text-blue-400 font-semibold">they lead the future.</span>
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8 px-4 w-full sm:w-auto"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                playSound('click');
                setQuizOpen(true);
              }}
              onMouseEnter={() => playSound('hover')}
              className="group relative w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm sm:text-base lg:text-lg font-bold rounded-lg overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative flex items-center justify-center gap-2">
                <Brain size={18} className="flex-shrink-0" />
                <span className="whitespace-nowrap">Take Brand Quiz</span>
              </div>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                playSound('click');
                scrollToSection('contact');
              }}
              onMouseEnter={() => playSound('hover')}
              className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm sm:text-base lg:text-lg font-bold rounded-lg neon-glow flex items-center justify-center gap-2"
            >
              <Sparkles size={18} className="flex-shrink-0" />
              <span className="whitespace-nowrap">Book Free Consultation</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                playSound('click');
                scrollToSection('services');
              }}
              onMouseEnter={() => playSound('hover')}
              className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 bg-transparent border-2 border-cyan-500 text-cyan-400 text-sm sm:text-base lg:text-lg font-bold rounded-lg hover:bg-cyan-500/10 transition-colors"
            >
              Explore Services
            </motion.button>
          </motion.div>

          {/* 3D Experience CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex justify-center pt-2 px-4"
          >
            <motion.button
              data-testid="hero-3d-experience-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                playSound('whoosh');
                navigate('/experience');
              }}
              onMouseEnter={() => playSound('hover')}
              className="hidden md:block group relative w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 bg-slate-900/60 backdrop-blur-md border border-cyan-400/40 text-cyan-300 text-sm sm:text-base font-bold rounded-lg overflow-hidden hover:border-cyan-400 transition-colors"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative flex items-center justify-center gap-2">
                <Orbit size={18} className="flex-shrink-0 animate-spin-slow" />
                <span className="whitespace-nowrap">Explore Our Universe · 3D</span>
              </div>
            </motion.button>
          </motion.div>

          {/* Trust Badge */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="pt-8 sm:pt-12 px-4"
          >
            <p className="text-xs sm:text-sm text-cyan-400/60 uppercase tracking-widest">
              Trusted by visionary businesses ready to scale
            </p>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="cursor-pointer"
            onClick={() => scrollToSection('services')}
          >
            <ChevronDown size={40} className="text-cyan-400" />
          </motion.div>
        </motion.div>
      </div>

      {/* Brand Quiz Modal */}
      <BrandQuiz
        isOpen={quizOpen}
        onClose={() => setQuizOpen(false)}
        onComplete={handleQuizComplete}
      />
    </section>
  );
};

export default Hero;
